import { ScrollToTop } from "@/shared/lib/ScrollToTop";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseBreadCrumb } from "@/shared/ui/base-bread-crumb";
import { BaseDivider } from "@/shared/ui/base-divider";
import { OfferCargoScheduleInfo } from "@/widgets/offer-cargo-schedule-info";
import { Box, Container, Stack, Typography } from "@mui/material";
import { Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import type { AdminCargoDetailPageProps } from "../../model";
import { CompanyInfo } from "../company-info";
import { CargoInfo } from "./cargo-info";
import { DeliveryProposalInfo } from "./delivery-proposal-info";
import { DestinationsInfo } from "./destinations-info";

export const CargoCreatePreview = ({
  previewValues,
  offerCargoProcess,
  onBackToInProgressClick,
  onSubmitClick,
  onCopyCargoClick,
}: AdminCargoDetailPageProps) => {
  const links = [
    { href: "/admin/cargos", label: "荷物一覧" },
    { label: "荷物詳細" },
  ];

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmitClick = useCallback(async () => {
    if (!previewValues) {
      return;
    }

    setIsSubmitting(true);
    await onSubmitClick(previewValues);
    setIsSubmitting(false);
  }, [onSubmitClick, previewValues]);

  if (!previewValues) {
    return (
      <Stack justifyContent="center" alignItems="center">
        <Typography variant="h6" color="error">
          荷物情報がありません
        </Typography>
      </Stack>
    );
  }

  return (
    <Box px={{ xs: 2, sm: 4 }}>
      <ScrollToTop />
      <BaseBreadCrumb links={links} sx={{ mb: 3 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
          }}
        >
          荷物詳細
        </Typography>
        <BaseButton
          startIcon={<BaseIcon name="plus" htmlColor="#52A48B" />}
          variant="outlined"
          onClick={onCopyCargoClick}
        >
          コピーして荷物登録する
        </BaseButton>
      </Box>

      <Box
        display="flex"
        mt={4}
        mb={2}
        bgcolor="grey.50"
        minHeight="600px"
        flexDirection={"column"}
        flexGrow={1}
        sx={{
          mx: { xs: -2, sm: 0 },
        }}
      >
        <Container
          maxWidth="md"
          sx={{
            bgcolor: "white",
            mt: 4,
            mb: 7,
            border: "1px solid",
            borderColor: "grey.200",
            borderRadius: "16px",
          }}
        >
          <Stack spacing={4} mt={3} mb={10} px={{ xs: 1, sm: 3 }}>
            <CompanyInfo offerCargoProcess={offerCargoProcess} />
            <BaseDivider />
            <OfferCargoScheduleInfo draftOfferCargo={previewValues} />
            <BaseDivider />
            <DestinationsInfo draftOfferCargo={previewValues} />
            <BaseDivider />
            <CargoInfo previewValues={previewValues} />
            <BaseDivider />
            <DeliveryProposalInfo deadlineAt={previewValues.deadlineAt} />
          </Stack>
        </Container>
      </Box>

      <Stack spacing={1} mb={2}>
        <Box display="flex" justifyContent="center">
          <Typography fontSize="12px">※ 更新内容を保存します</Typography>
        </Box>
        <Stack direction="row" spacing={1} justifyContent="center">
          <BaseButton
            LinkComponent={Link}
            variant="outlined"
            color="primary"
            sx={{ width: "96px", fontSize: "14px", fontWeight: 700 }}
            onClick={onBackToInProgressClick}
          >
            修正する
          </BaseButton>
          <BaseButton
            variant="contained"
            color="primary"
            sx={{ width: "240px", fontSize: "14px", fontWeight: 700 }}
            onClick={handleSubmitClick}
            disabled={isSubmitting}
          >
            更新する
          </BaseButton>
        </Stack>
      </Stack>
    </Box>
  );
};
